'use client';

import { Navbar } from '@/components/navigation/Navbar';
import { Footer } from '@/components/navigation/Footer';
import { Section } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Mail, MessageSquare, Send } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { validateContactForm } from '@/lib/security';
import { areCookiesAllowed } from '@/lib/cookieConsent';

export default function ContactPage() {
  const t = useTranslations('contact');
  const tForm = useTranslations('contact.form');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [cookiesAllowed, setCookiesAllowed] = useState<boolean>(true);

  // Fetch CSRF token on mount and check cookie consent
  useEffect(() => {
    const cookiesAllowedStatus = areCookiesAllowed();
    setCookiesAllowed(cookiesAllowedStatus);
    
    fetch('/api/csrf')
      .then(res => res.json())
      .then(data => {
        setCsrfToken(data.token);
        // Update cookies allowed status from server response
        if (data.cookiesAllowed !== undefined) {
          setCookiesAllowed(data.cookiesAllowed);
        }
      })
      .catch(() => {
        // If CSRF fails, still allow form submission (graceful degradation)
        console.warn('Failed to fetch CSRF token');
      });
  }, []);

  // Client-side validation using security utilities
  const validateForm = () => {
    const validation = validateContactForm(formData);
    
    if (!validation.isValid) {
      // Map validation errors to translated messages
      const translatedErrors: Record<string, string> = {};
      
      if (validation.errors.name) {
        translatedErrors.name = validation.errors.name.includes('requis') 
          ? tForm('nameRequired')
          : validation.errors.name.includes('caractères non autorisés')
          ? tForm('unauthorizedContent')
          : tForm('nameMaxLength');
      }
      
      if (validation.errors.email) {
        translatedErrors.email = validation.errors.email.includes('requis')
          ? tForm('emailRequired')
          : validation.errors.email.includes('invalide')
          ? tForm('emailInvalid')
          : tForm('emailMaxLength');
      }
      
      if (validation.errors.message) {
        translatedErrors.message = validation.errors.message.includes('requis')
          ? tForm('messageRequired')
          : validation.errors.message.includes('10 caractères')
          ? tForm('messageMinLength')
          : validation.errors.message.includes('5000 caractères')
          ? tForm('messageMaxLength')
          : tForm('unauthorizedContent');
      }
      
      if (validation.errors.general) {
        translatedErrors.general = tForm('unauthorizedContent');
      }
      
      setErrors(translatedErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      setStatus('error');
      return;
    }

    setStatus('sending');

    try {
      // Send to secure API endpoint
      // CSRF token is required, but validation is adapted based on cookie consent
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '' // Always send token if available
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle API errors
        if (response.status === 429) {
          setErrors({ general: 'Trop de requêtes. Veuillez réessayer plus tard.' });
        } else if (response.status === 403) {
          setErrors({ general: 'Erreur de sécurité. Veuillez rafraîchir la page.' });
          // Refresh CSRF token
          fetch('/api/csrf')
            .then(res => res.json())
            .then(data => setCsrfToken(data.token));
        } else if (data.errors) {
          setErrors(data.errors);
        } else {
          setErrors({ general: data.error || tForm('error') });
        }
        setStatus('error');
        return;
      }
      
      // Reset form on success
      setFormData({ name: '', email: '', message: '' });
      setStatus('success');
      
      // Refresh CSRF token after successful submission
      fetch('/api/csrf')
        .then(res => res.json())
        .then(data => setCsrfToken(data.token));
        
    } catch (error) {
      setStatus('error');
      setErrors({ general: tForm('errorGeneral') });
    }
  };

  return (
    <>
      <Navbar />
      <main>
        <Section variant="gradient" className="pt-32 pb-20">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-bold leading-normal mb-6 bg-primary bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <p className="text-2xl text-foreground max-w-3xl mx-auto">
              {t('subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <Card className="bg-white/90">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {t('info.title')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-accent mt-1" />
                  <div>
                    <p className="text-foreground mb-1">{t('info.email')}</p>
                    <a href={`mailto:${t('info.emailValue')}`} className="text-foreground hover:text-accent transition-colors break-all">
                      {t('info.emailValue')}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MessageSquare className="w-6 h-6 text-secondary mt-1" />
                  <div>
                    <p className="text-foreground mb-1">{t('info.support')}</p>
                    <p className="text-foreground">{t('info.supportText')}</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white/90">
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                {errors.general && (
                  <div className="p-3 bg-danger/20 border border-danger/40 rounded-lg">
                    <p className="text-danger text-sm">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-foreground mb-2">
                    {tForm('name')} <span className="text-danger">{tForm('required')}</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) setErrors({ ...errors, name: '' });
                    }}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-foreground focus:outline-none transition-colors ${
                      errors.name 
                        ? 'border-danger' 
                        : 'border-accent/30 focus:border-accent'
                    }`}
                    required
                    maxLength={100}
                    autoComplete="name"
                  />
                  {errors.name && (
                    <p className="text-danger text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-foreground mb-2">
                    {tForm('email')} <span className="text-danger">{tForm('required')}</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (errors.email) setErrors({ ...errors, email: '' });
                    }}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-foreground focus:outline-none transition-colors ${
                      errors.email 
                        ? 'border-danger' 
                        : 'border-accent/30 focus:border-accent'
                    }`}
                    required
                    maxLength={255}
                    autoComplete="email"
                  />
                  {errors.email && (
                    <p className="text-danger text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-foreground mb-2">
                    {tForm('message')} <span className="text-danger">{tForm('required')}</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value });
                      if (errors.message) setErrors({ ...errors, message: '' });
                    }}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-foreground focus:outline-none transition-colors resize-none ${
                      errors.message 
                        ? 'border-danger' 
                        : 'border-accent/30 focus:border-accent'
                    }`}
                    required
                    minLength={10}
                    maxLength={5000}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.message && (
                      <p className="text-danger text-sm">{errors.message}</p>
                    )}
                    <p className={`text-sm ml-auto ${formData.message.length > 5000 ? 'text-danger' : 'text-foreground'}`}>
                      {formData.message.length}{tForm('charCount')}
                    </p>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full group" 
                  disabled={status === 'sending' || status === 'success'}
                >
                  {status === 'sending' ? (
                    tForm('sending')
                  ) : status === 'success' ? (
                    tForm('success')
                  ) : (
                    <>
                      {tForm('send')}
                      <Send className="ml-2 w-5 h-5 inline-block group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>

                {status === 'error' && !errors.general && (
                  <p className="text-danger text-sm text-center">
                    {tForm('error')}
                  </p>
                )}
              </form>
            </Card>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
